import React, { useEffect, useState, useRef, MutableRefObject } from "react";
import style from "../style.module.scss";
import "./style.module.scss";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import classnames from "classnames";
import { convertSecondsToTimeString } from "@jmc/utils/utils/date-to-string";
import ReactPlayerLoader from "@brightcove/react-player-loader";
import { MediaStatus } from "../MediaStatus";
import isEqual from "lodash/isEqual";
import { BrightcovePreview } from "@jmc/solid-design-system/src/components/organisms/VideoPlayer/Brightcove/BrightcovePreview";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";

export interface Props {
    /** The brightcove video id */
    videoId: string;
    /** Current Playing Video ref*/
    playVideoRef?: MutableRefObject<any>;
    /** A title that is shown when the video is not playing yet or paused */
    title?: string;
    /** Brightcove account id */
    accountId?: string;
    /** Brightcove application id */
    applicationId?: string;
    /** Brightcove player id */
    playerId?: string;
    /** Should the player be playable? */
    videoPlayable?: boolean;
    /** Controls the offset of the video in seconds */
    offset?: number;
    /** A Background image that is shown when the video is not playing yet */
    thumbnail?: string;
    /** Should the video play automatically */
    autoplay?: boolean;
    /** Command to toggle playing mode */
    play?: boolean;
    /** a callback triggered every 15-250ms while video is playing. See https://docs.brightcove.com/brightcove-player/1.x/Player.html#progress-event */
    onTimeUpdate?: () => void;
    /** What happens when the video starts playing */
    onPlay?: (videoref: MutableRefObject<any> | null) => void;
    /** Should video have an overlay when not playing? */
    hasOverlay?: boolean;
    /** Alignment of summary and title */
    contentAlignment?: "left" | "center" | "right";
    contentVerticalAlignment?: "top" | "center" | "bottom";
    /** Should the video player controls be visible? */
    showControls?: boolean;
    /** Should the big play button be visible? */
    showLargePlayButton?: boolean;
    /** Should the duration be shown when the video is loaded? */
    showDuration?: boolean;
    /** Check if its audio or not */
    isAudio?: boolean;
    /** Display a warning if the video cannot be displayed because the cookies are not accepted */
    warningMessage?: JSX.Element;
    /** Check if videoplayer is shown in header ex. to set title to h2 */
    showInHeader?: boolean;
    /** Check time when paused and send to Parent element Cardimpression */
    mediastop?: (status: MediaStatus, currentTime: number | string) => void;
    /** Get total length time */
    getLength?: (duration: number) => void;
    /** Get cookie acceptance (Optanon) status */
    cookieAccept?: (accepted: boolean) => void;
    /** Get current time of player */
    getCurrentTime?: (currentTime: number) => void;
    /** SEO title */
    seoTitle?: string;
    /** Alt tag */
    alt?: string;
    imageFocus?: ImagePosition;
    className?: string;
    considerFluidStyle?: boolean;
    iosFullWindow?: boolean;
    startPlaying?: boolean; // Set to true when the coming from the preloader.
}

export const Brightcove: React.FunctionComponent<Props> = ({
    videoId,
    playVideoRef,
    title,
    accountId = "1475651728001",
    playerId = "k6NrfLHmz",
    applicationId = "Janssen Medical Cloud 2.0",
    videoPlayable = true,
    offset = 0,
    thumbnail,
    autoplay = false,
    play = false,
    onTimeUpdate,
    onPlay,
    hasOverlay = false,
    contentAlignment = "center",
    contentVerticalAlignment = "center",
    showControls = true,
    showLargePlayButton = true,
    showDuration = false,
    isAudio = false,
    warningMessage = undefined,
    mediastop = null,
    getLength = null,
    getCurrentTime = null,
    seoTitle = null,
    cookieAccept = null,
    imageFocus = null,
    considerFluidStyle = false, //specify wether or not the video is on a detail page in jmc
    iosFullWindow = false, // Plays iOS videos in full window instead of fullscreen
    startPlaying = false,
}: Props) => {
    const [playing, setPlaying] = React.useState(false);
    const [playerApi, setPlayerApi] = React.useState(null);
    const [duration, setDuration] = React.useState("");
    const [durationIsVisible, setDurationIsVisible] = React.useState(showDuration);
    const [accepted, setAccepted] = useState("loading");
    const videoref = useRef(null);

    React.useEffect(() => {
        if (!playerApi) return;

        const poster = playerApi.posterImage;

        if (imageFocus) {
            const el = poster.el();
            el.style.backgroundPosition = imageFocus.replace("-", " ");
        }

        playerApi.on("posterchange", () => {
            if (hasOverlay) {
                const el = poster.el();
                const bg = el.style.backgroundImage;
                el.style.backgroundImage = `linear-gradient(0deg, rgba(20, 20, 20, 0.38), rgba(20, 20, 20, 0.38)), ${bg}`;
            }
        });

        playerApi.on("play", () => {
            mediastop && mediastop(MediaStatus.PLAY, playerApi.currentTime());
            setPlaying(true);
            showDuration && setDurationIsVisible(false);
            onPlay && onPlay(videoref);
        });
        playerApi.on("pause", () => {
            mediastop && mediastop(MediaStatus.PAUSE, playerApi.currentTime());
            showDuration && setDurationIsVisible(true);
            setPlaying(false);
        });
        playerApi.on("timeupdate", () => {
            getCurrentTime && getCurrentTime(playerApi.currentTime());
            onTimeUpdate && onTimeUpdate();
        });
        playerApi.on("ready", () => {
            autoplay && playerApi.muted(true);
            autoplay && playerApi.autoplay(true);
            autoplay && mediastop && mediastop(MediaStatus.PLAY, playerApi.currentTime());
        });

        playerApi.on("loadedmetadata", () => {
            const rawDuration = convertSecondsToTimeString(playerApi.duration());
            setDuration(rawDuration.replace(/^0/, ""));
            getLength && getLength(playerApi.duration());
        });

        playerApi.on("touchstart", () => {
            if (showControls) {
                return;
            }

            if (playerApi.paused() && playerApi.hasStarted()) {
                playerApi.play();
            } else {
                playerApi.pause();
            }
        });

        if (startPlaying && playerApi) {
            setTimeout(() => {
                playerApi.play();
            }, 400);
        }
    }, [playerApi]);

    React.useEffect(() => {
        if (!playerApi) return;
        playerApi?.currentTime(offset);
    }, [playerApi, offset]);

    React.useEffect(() => {
        playerApi && playerApi.play();
    }, [offset]);

    React.useEffect(() => {
        if (!playerApi) return;
        play ? playerApi?.play() : playerApi?.pause();
    }, [playerApi, play]);

    React.useEffect(() => {
        if (!playVideoRef) return;
        if (playerApi && !isEqual(playVideoRef, videoref) && playing) {
            playerApi.pause();
        }
    }, [playVideoRef, playerApi]);

    useEffect(() => {
        const scriptStatus = (): void => {
            const activeGroups = (window as any)?.OptanonActiveGroups as string;
            const currentStatus =
                activeGroups?.split(",")?.includes(`${CookieConsent.FUNTIONAL_COOKIES}`) || activeGroups === undefined
                    ? "ready"
                    : "blocked";
            setAccepted(currentStatus);
        };

        scriptStatus();
        window.addEventListener("OptanonActiveGroupsChanged", scriptStatus);
        return () => {
            window.removeEventListener("OptanonActiveGroupsChanged", scriptStatus);
        };
    });

    //have no access to videojs element directly, so finding it via use of callback ref pattern
    //we have very limited access to video-js from the ReactPlayerLoader component, and so most props for it must be accessed from here
    const callbackRef = (node: HTMLInputElement) => {
        if (node) {
            const videojs = node.getElementsByTagName("video-js")[0];
            const video = node.getElementsByTagName("video")[0];
            videojs.classList.remove("vjs-play-button-position-top-left");
            videojs.classList.add("vjs-big-play-centered");
            videojs.setAttribute("title", seoTitle ?? title);
            videojs.setAttribute("data-player", playerId);
            if (considerFluidStyle) {
                videojs.classList.add("vjs-fluid");
            }
            if (!considerFluidStyle) {
                videojs.classList.remove("vjs-fluid");
            }
            if (video) {
                video.setAttribute("playsinline", "true");
                video.setAttribute("preload", "none"); // Set preload to none
            }
        }
    };

    const onSuccess = (success: any) => {
        setPlayerApi(success.ref);
        cookieAccept && cookieAccept(true);
    };

    //slightly lower the vertical alignement of text if the big play button is shown to prevent overlap
    const verticalAlignement =
        contentVerticalAlignment === "center" && showLargePlayButton ? "center_button" : contentVerticalAlignment;

    // display preview during SSR and as long as videojs is not loaded.
    const SSR = typeof window === "undefined";
    const showPreview = SSR || !playerApi;

    return (
        <div ref={videoref}>
            <div
                data-test-id={`VideoPlayer.${videoId}`}
                ref={accepted === "ready" ? callbackRef : null}
                className={classnames(
                    style.brightcove,
                    !considerFluidStyle && style.flex,
                    videoPlayable ? style.player : style.disabled,
                    !isAudio || thumbnail ? style.videoPlayer : style.ajs,
                    !showControls && style.hideControls,
                    !showLargePlayButton || (!thumbnail && isAudio) ? style.hidePlay : style.playButton,
                    accepted === "blocked" && style.blocked,
                )}
            >
                {accepted === "blocked" && warningMessage && (
                    <div id={"superduperid"} className={style.warning}>
                        {warningMessage}
                    </div>
                )}

                {showPreview && <BrightcovePreview thumbnail={thumbnail} />}

                {accepted === "ready" && (
                    <ReactPlayerLoader
                        accountId={accountId}
                        videoId={videoId}
                        onSuccess={onSuccess}
                        poster={thumbnail}
                        applicationId={applicationId}
                        playerId={playerId}
                        options={
                            iosFullWindow && /iPhone|iPod|iPad/.test(navigator.platform)
                                ? { preferFullWindow: true }
                                : null
                        }
                    />
                )}

                {!playing && (
                    <div
                        className={classnames(
                            isAudio && !thumbnail ? style.player__hide : style.player__content,
                            style[`player__content__alignment__horizontal_${contentAlignment}`],
                            style[`player__content__alignment__vertical_${verticalAlignement}`],
                        )}
                    >
                        {durationIsVisible && (
                            <div className={classnames(style["duration"])}>
                                <div className={classnames(style["duration__display"])}>{duration}</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Brightcove;
